<template>
  <div>
    <app-switcher-icon
      v-if="hasMultipleApps || hasSupport"
      :has-support="hasSupport"
      :has-multiple-apps="hasMultipleApps"
      @on-click-support="onSupportClick"
    />
    <apps-panel v-if="hasMultipleApps" target-id="apps-list-open-icon" :apps="getAppsUrl" @on-click-app="onAppClick" />
  </div>
</template>

<script>
import AppsPanel from './AppsPanel.vue'
import AppSwitcherIcon from './AppSwitcherIcon.vue'
import { decodeJWT } from '@/utils/jwt-utils.js'
import { getAccessToken } from '@/plugins/authentication'
import { capitalize } from '@/utils/formatter'

// Rule object for extracting identifiers
const appIdentifierRules = {
  fp: {
    extractApp: (app) => app.split('/')[0],
    extractCurrent: (host) => host,
    extractTenant: (app) => capitalize(app.split('.fairmat.com')[0]),
  },
  okv: {
    extractApp: (app) => app.split('/')[0],
    extractCurrent: (host) => host,
    extractTenant: (app) => capitalize(app.split('.fairmat.com')[0]),
  },
  pd: {
    extractApp: (app) => app.split('/')[0],
    extractCurrent: (host) => host,
    extractTenant: (app) => capitalize(app.split('-investments')[0]),
  },
  bt: {
    extractApp: (app) => app.split('/')[0],
    extractCurrent: (host) => host,
    extractTenant: (app) => capitalize(app.split('-dashboard-queue')[0]),
  },
  pm: {
    extractApp: (app) => app.split('/')[0],
    extractCurrent: (host) => host,
    extractTenant: (app) => capitalize(app.split('-targetmarket')[0]),
  },
  dds: {
    extractApp: (app) => {
      const parts = app.split('/')
      const domain = parts[0]
      const path = parts[1] || ''
      return path ? `${domain}/${path}` : domain
    },
    extractCurrent: (host, path) => {
      const pathParts = path.split('/')
      const domain = host
      const firstPath = pathParts[0] || ''
      return firstPath ? `${domain}/${firstPath}` : domain
    },
    extractTenant: (app) => {
      const parts = app.split('/')
      const tenant = parts[1]
      const tenantParts = tenant.split('-').map((part) => capitalize(part))
      return tenantParts.join(' ')
    },
  },
  itsm: {
    extractApp: (app) => app,
    extractCurrent: () => '',
  },
}

export default {
  components: {
    AppsPanel,
    AppSwitcherIcon,
  },
  data() {
    return {
      appsList: [],
      supportUrl: null,
    }
  },
  computed: {
    hasMultipleApps() {
      return this.appsList.length > 0
    },
    hasSupport() {
      return !!this.supportUrl
    },
    getAppsUrl() {
      const appCounts = new Map()
      this.appsList.forEach((el) => {
        const [prefix, app] = el.split('_')
        const url = `https://${app}`
        const rule = appIdentifierRules[prefix]
        if (!rule) return // Skip if no rule defined
        // retrieve tenant instances
        const currentTenant = appCounts.get(prefix) || {
          name: this.$t(`appSwitcher.${prefix}.name`),
          description: this.$t(`appSwitcher.${prefix}.description`),
          tenant: [],
        }
        const tenantData = {
          tenant: rule.extractTenant(app),
          url,
        }
        currentTenant.tenant.push(tenantData)
        appCounts.set(prefix, currentTenant)
      })
      return Array.from(appCounts.values())
    },
  },
  methods: {
    onAppClick(url) {
      window.open(url, '_blank')
    },
    onSupportClick() {
      window.open(this.supportUrl, '_blank')
    },
    loadApps() {
      const jwtToken = this.getJWTfromLocalStorage()
      if (!jwtToken) {
        return []
      }
      const jwtData = decodeJWT(jwtToken)
      const realmAccessRoles =
        jwtData && jwtData.realm_access && jwtData.realm_access.roles ? jwtData.realm_access.roles : []

      const domainRoles = realmAccessRoles.filter((role) => role.includes('fairmat.com') || role.includes('itsm_'))
      // Remove current app from the list
      const domainRolesWithoutCurrent = this.removeCurrentApp(domainRoles)
      // Determine support URL from ITSM role if available
      const itsmRole = domainRolesWithoutCurrent.find((role) => role.includes('itsm'))
      if (itsmRole) {
        const [, supportDomain] = itsmRole.split('_')
        this.supportUrl = `https://${supportDomain}`
      }

      return domainRolesWithoutCurrent.filter((role) => !role.includes('itsm')).sort()
    },
    getJWTfromLocalStorage() {
      return getAccessToken() || null
    },
    removeCurrentApp(list) {
      const currentHost = window.location.hostname
      const currentPath = window.location.pathname
      return list.filter((el) => {
        const [prefix, app] = el.split('_')
        const rule = appIdentifierRules[prefix]
        if (!rule) return true // If no rule, don't filter out
        const appIdentifier = rule.extractApp(app)
        const currentIdentifier = rule.extractCurrent(currentHost, currentPath)
        return appIdentifier !== currentIdentifier
      })
    },
  },
  mounted() {
    this.appsList = this.loadApps()
  },
}
</script>
